import { useRef } from "react";
import { Box } from "@mui/material";
import { Color, DotLegalChip, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import MoreItemsBadge from "@common/components/moreItemsBadge/MoreItemsBadge";
import { useIsOverflowing } from "@common/hooks/useIsOverflowing";

export interface ColoredChipItem {
    name: string;
    color?: Color;
}

export interface IColoredChipsCell {
    items: Array<ColoredChipItem>;
    // A table cell shows one chip so every row stays one line high. The entity top bar tiles have room for more.
    visibleCount?: number;
}

export function ColoredChips({ items }: { items: Array<ColoredChipItem> }) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {items.map((item, index) => (
                <DotLegalChip key={`${item.name}-${index}`} value={item.name} color={item.color} size="small" isSelected />
            ))}
        </Box>
    );
}

// Names the chip on hover only when it has shrunk enough to cut its name short.
function ShrinkableChip({ item }: { item: ColoredChipItem }) {
    const chipRef = useRef<HTMLDivElement>(null);
    const isNameCutShort = useIsOverflowing(chipRef, item.name, ".MuiChip-label");

    return (
        <DotLegalTooltip text={item.name} tooltipStyle="primary" useBoldText disableHoverlistener={!isNameCutShort}>
            {/* As flex boxes the wrappers are only as tall as the chip, where text layout would add line spacing below
                it and the "+N" would center on the taller wrapper instead of the chip. */}
            <Box ref={chipRef} sx={{ minWidth: 0, display: "flex", "& > *": { display: "flex", minWidth: 0 } }}>
                <DotLegalChip value={item.name} color={item.color} size="small" isSelected />
            </Box>
        </DotLegalTooltip>
    );
}

// Business areas and tags in a table cell. The rest used to collapse into unlabelled colored dots, which the reader
// could not tell apart, so they now sit behind a "+N" whose tooltip names them.
function ColoredChipsCell({ items, visibleCount = 1 }: IColoredChipsCell) {
    if (items.length === 0) {
        return null;
    }

    const visibleItems = items.slice(0, visibleCount);
    const hiddenItemCount = items.length - visibleItems.length;

    return (
        // A top bar tile is fixed in size and clips what overflows it, so the chips shrink and cut their names short
        // (full name on hover) rather than spill out of the tile.
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {visibleItems.map((item, index) => (
                <ShrinkableChip key={`${item.name}-${index}`} item={item} />
            ))}
            {hiddenItemCount > 0 && <MoreItemsBadge count={hiddenItemCount} tooltip={<ColoredChips items={items} />} />}
        </Box>
    );
}

export default ColoredChipsCell;
